import { useStartSolarGeometryGeneration } from '@/api/mutations/project/useStartSolarGeometryGeneration';
import { useCreateSolarDesign } from '@/api/mutations/project/useCreateSolarDesign';
import useGetMyAccountFeatures from '@/api/queries/me/useGetMyAccountFeatures';
import useGetProjectByIdOverview from '@/api/queries/project/useGetProjectByIdOverview';
import useGetProjectSolarPotential, {
  type ProjectSolarPotential,
} from '@/api/queries/project/useGetProjectSolarPotential';
import { useSolarDesignerState } from '@/api/queries/project/useSolarDesignerState';
import AuthenticatedPageWrapper from '@/ui/layouts/authenticatedPageWrapper/AuthenticatedPageWrapper';
import { type BreadcrumbItem, Button, EmptyState } from '@payaca/ambrosia';
import LoaderOverlay from '@payaca/components/loaderOverlay/LoaderOverlay';
import { type SolarLatLng } from '@payaca/components/solarRoofView/SolarRoofView';
import { solarDesignDraftStateSchema } from '@payaca/helpers/solarDesignDraft';
import {
  type FC,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Redirect, useHistory, useLocation, useParams } from 'react-router-dom';
import ConfirmLocationView from './solarDesigner/ConfirmLocationView';
import { chooseDesignSource } from './solarDesigner/chooseDesignSource';
import { manualDesignSource } from './solarDesigner/manualDesignSource';
import SolarDesignerContent from './SolarDesignerContent';

/** Save a separate design before leaving an explicitly requested analysis. */
const NewAnalysedDesign: FC<{
  projectId: string;
  projectPublicId: string;
  source: ProjectSolarPotential;
}> = (props) => {
  const { projectId, source } = props;
  const history = useHistory();
  const createDesign = useCreateSolarDesign(projectId, props.projectPublicId);
  const started = useRef(false);
  const { mutate } = createDesign;
  const create = useCallback(() => {
    const designId = crypto.randomUUID();
    mutate(
      {
        designId,
        name: 'Solar design',
        description: '',
        basis: 'ANALYSIS',
        source,
      },
      {
        onSuccess: () =>
          history.replace(
            `/projects/${projectId}/solar-designer?design=${designId}`
          ),
      }
    );
  }, [history, mutate, projectId, source]);
  useEffect(() => {
    if (started.current) return;
    started.current = true;
    create();
  }, [create]);
  if (createDesign.isError) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-4 p-8">
        <EmptyState text="The roof was analysed, but the new design could not be saved." />
        <Button size="sm" onClick={create}>
          Retry save
        </Button>
      </div>
    );
  }
  return <LoaderOverlay />;
};

const SolarDesigner: FC<{ projectId: string }> = (props) => {
  const { projectId } = props;
  const history = useHistory();
  const location = useLocation<{ manualDesignCentre?: SolarLatLng }>();
  const params = new URLSearchParams(location.search);
  const designId = params.get('design') ?? undefined;
  const explicitAnalysis = !designId && params.get('analyse') === '1';
  const { data: features, isLoading: isLoadingFeatures } =
    useGetMyAccountFeatures();
  const canDesignSolar =
    features?.includes('hasEstimates') &&
    features.includes('hasSolarDesignTool');
  const { project, refetch: refetchProject } =
    useGetProjectByIdOverview(projectId);
  const { solarPotential, isLoading: isLoadingPotential } =
    useGetProjectSolarPotential(canDesignSolar ? projectId : undefined);
  const designerState = useSolarDesignerState(
    projectId,
    !!canDesignSolar,
    designId
  );
  // An explicit analysis creates a separate design, never replaces the default draft.
  const draft = explicitAnalysis
    ? undefined
    : designerState.data?.projectById.solarDesignDraft;
  const manualDraft = draft?.basis === 'MANUAL' ? draft : null;
  const generation = designerState.data?.projectById.solarGeometryGeneration;
  const [chosenSource, setChosenSource] =
    useState<ProjectSolarPotential | null>(null);
  const [requestedRunId, setRequestedRunId] = useState<string | null>(
    explicitAnalysis ? params.get('run') : null
  );
  const [hasRequestedAnalysis, setHasRequestedAnalysis] =
    useState(!!requestedRunId);
  const startGeneration = useStartSolarGeometryGeneration(
    project?.publicId ?? '',
    projectId
  );
  const createManualDesign = useCreateSolarDesign(projectId, project?.publicId);
  const onDrawManually = async (centre: SolarLatLng) => {
    const newDesignId = crypto.randomUUID();
    try {
      await createManualDesign.mutateAsync({
        designId: newDesignId,
        name: 'Hand-drawn roof',
        description: '',
        basis: 'MANUAL',
      });
      history.push(
        `/projects/${projectId}/solar-designer?design=${newDesignId}`,
        {
          manualDesignCentre: centre,
        }
      );
    } catch {
      // Keep the selected pin and show the retryable error in confirmation.
    }
  };
  const generating =
    generation?.status === 'QUEUED' || generation?.status === 'RUNNING';
  const isAnalysing = startGeneration.isPending || generating;
  const parsedDraft = useMemo(
    () => solarDesignDraftStateSchema.safeParse(draft?.state),
    [draft?.state]
  );

  const siteAddressCoordinates = project?.addresses.site[0]?.coordinates;
  const siteAddressCentre = useMemo<SolarLatLng | undefined>(
    () =>
      siteAddressCoordinates
        ? {
            lat: siteAddressCoordinates.latitude,
            lng: siteAddressCoordinates.longitude,
          }
        : undefined,
    [siteAddressCoordinates]
  );

  const manualSource = useMemo(
    () =>
      manualDraft && parsedDraft.success && siteAddressCentre
        ? manualDesignSource(
            location.state?.manualDesignCentre ?? siteAddressCentre,
            manualDraft
          )
        : null,
    [
      manualDraft,
      parsedDraft.success,
      siteAddressCentre,
      location.state?.manualDesignCentre,
    ]
  );

  useEffect(() => {
    if (draft && !parsedDraft.success) return;
    if (manualDraft) return;
    if (
      requestedRunId &&
      generation?.id === requestedRunId &&
      (generation.status === 'READY' || generation.status === 'FAILED')
    ) {
      setChosenSource(
        generation.recoveryReason ? null : (generation.result ?? null)
      );
      setRequestedRunId(null);
    } else if (
      !explicitAnalysis &&
      !chosenSource &&
      !designerState.isLoading &&
      !isLoadingPotential &&
      !isAnalysing
    ) {
      const source = chooseDesignSource(solarPotential, draft, generation, {
        preferSavedDesign: !!designId,
      });
      if (source?.available && source.centre) setChosenSource(source);
    }
  }, [
    chosenSource,
    designId,
    explicitAnalysis,
    designerState.isLoading,
    draft,
    parsedDraft.success,
    manualDraft,
    generation,
    isAnalysing,
    isLoadingPotential,
    requestedRunId,
    solarPotential,
  ]);

  const requestGeneration = useCallback(
    (
      input: {
        projectId: string;
        location?: SolarLatLng;
        forceRefresh?: boolean;
      },
      options?: { onSettled?: () => void }
    ) => {
      setHasRequestedAnalysis(true);
      startGeneration.mutate(
        {
          location: input.location,
          retry: generation?.status === 'FAILED',
          forceRefresh: input.forceRefresh,
        },
        {
          onSuccess: (data) =>
            setRequestedRunId(data.startSolarGeometryGeneration.id),
          onSettled: options?.onSettled,
        }
      );
    },
    [startGeneration, generation?.status]
  );

  const onExit = () => history.push('/projects/' + projectId + '/design');
  const breadcrumbItems: BreadcrumbItem[] = [
    {
      label: project?.pipeline.name ?? 'Pipeline',
      to: project?.pipeline
        ? '/pipelines/' + project.pipeline.publicId
        : undefined,
    },
    {
      label: project ? 'Project: #' + project.reference : 'Project',
      to: '/projects/' + projectId + '/design',
    },
    { label: draft?.name ?? 'Solar designer' },
  ];

  if (!isLoadingFeatures && !canDesignSolar)
    return <Redirect to={'/projects/' + projectId} />;

  const source =
    chosenSource?.available &&
    chosenSource.centre &&
    !chosenSource.roofRecoveryReason
      ? chosenSource
      : null;
  // A current draft saved before content-only versions carries the legacy
  // version; the server then reports the analysis it still matches.
  const sourceMatchesDraft =
    draft?.sourceAnalysisId === source?.sourceAnalysisId &&
    (draft?.sourceAnalysisVersion === source?.sourceAnalysisVersion ||
      (!!draft?.analysis &&
        draft.analysis.sourceAnalysisVersion ===
          source?.sourceAnalysisVersion)) &&
    !draft?.isStale;
  const needsInitialAnalysis =
    source &&
    !source.geometry &&
    generation?.status !== 'READY' &&
    generation?.status !== 'FAILED';
  let generationError = startGeneration.isError
    ? 'The roof analysis could not be started. Please try again.'
    : null;
  if (
    generation?.status === 'FAILED' &&
    (!explicitAnalysis || hasRequestedAnalysis)
  )
    generationError = generation.error ?? null;
  let body: React.ReactNode;
  if (
    isLoadingFeatures ||
    isLoadingPotential ||
    designerState.isLoading ||
    !project
  ) {
    body = <LoaderOverlay />;
  } else if (
    (designerState.isError && !designerState.data) ||
    (draft && !parsedDraft.success)
  ) {
    body = (
      <EmptyState text="The saved design could not be loaded. Reload the page before editing." />
    );
  } else if (designId && !draft) {
    body = (
      <EmptyState text="This design could not be found. Return to the project's Design tab to choose a design." />
    );
  } else if (explicitAnalysis && source) {
    body = (
      <NewAnalysedDesign
        projectId={projectId}
        projectPublicId={project.publicId}
        source={source}
      />
    );
  } else if (manualDraft && manualSource) {
    body = (
      <SolarDesignerContent
        key={`manual:${manualDraft.id}`}
        basis="MANUAL"
        projectId={projectId}
        projectPublicId={project.publicId}
        designId={manualDraft.id}
        queryDesignId={designId}
        solarPotential={manualSource}
        onExit={onExit}
        initialDraft={
          !manualDraft.isStale && parsedDraft.success
            ? parsedDraft.data
            : undefined
        }
        draftRevision={manualDraft.revision}
        draftName={manualDraft.name}
        draftDescription={manualDraft.description}
        draftUsedByEstimate={manualDraft.isUsedByEstimate}
        staleDraft={manualDraft.isStale}
        siteAddressId={project.addresses.site[0]?.id}
        household={
          designerState.data?.projectById.addresses.site[0]?.propertyInformation
        }
        postcode={designerState.data?.projectById.addresses.site[0]?.postalCode}
        generateSolarGeometry={requestGeneration}
        isGeneratingGeometry={false}
        generationError={null}
        designerStateRefreshFailed={designerState.isRefetchError}
      />
    );
  } else if (manualDraft) {
    body = (
      <div className="flex h-full flex-col items-center justify-center gap-4 p-8">
        <EmptyState text="The project needs a geocoded site address before its hand-drawn roof can be shown on the map." />
        <Button size="sm" variant="secondary" onClick={onExit}>
          Back to the project
        </Button>
      </div>
    );
  } else if (
    source &&
    !needsInitialAnalysis &&
    !requestedRunId &&
    !startGeneration.isPending
  ) {
    body = (
      <SolarDesignerContent
        key={`${designId ?? 'default'}:${source.sourceAnalysisId}:${source.sourceAnalysisVersion}`}
        projectId={projectId}
        projectPublicId={project.publicId}
        designId={draft?.id}
        queryDesignId={designId}
        solarPotential={source}
        onExit={onExit}
        initialGeneratedWithoutGeometry={!source.geometry}
        initialDraft={parsedDraft.success ? parsedDraft.data : undefined}
        recoverySite={project.addresses.site[0]}
        readRecoverySource={async () => {
          const [latest, overview] = await Promise.all([
            designerState.refetch({ throwOnError: true }),
            refetchProject({ throwOnError: true }),
          ]);
          const current = latest.data?.projectById;
          const run = current?.solarGeometryGeneration;
          if (run?.status === 'QUEUED' || run?.status === 'RUNNING') {
            throw new Error(
              'Roof analysis is still running. Your layout is still here; try again when it finishes.'
            );
          }
          return {
            source: current?.solarDesignDraft?.analysis ?? run?.result ?? null,
            site: overview.data?.projectById.addresses.site[0],
          };
        }}
        onRecoveredSource={setChosenSource}
        draftRevision={draft?.revision ?? 0}
        draftName={draft?.name ?? 'Solar design'}
        draftDescription={draft?.description}
        draftUsedByEstimate={draft?.isUsedByEstimate}
        staleDraft={!!draft && !sourceMatchesDraft}
        savedSource={
          draft
            ? {
                sourceAnalysisId: draft.sourceAnalysisId ?? null,
                site: draft.sourceSite
                  ? {
                      id: draft.sourceSite.addressId,
                      coordinates: draft.sourceSite.coordinates ?? null,
                    }
                  : null,
                staleReason: draft.staleReason ?? null,
              }
            : undefined
        }
        siteAddressId={project?.addresses.site[0]?.id}
        household={
          designerState.data?.projectById.addresses.site[0]?.propertyInformation
        }
        postcode={designerState.data?.projectById.addresses.site[0]?.postalCode}
        generateSolarGeometry={requestGeneration}
        isGeneratingGeometry={isAnalysing}
        generationError={generationError}
        designerStateRefreshFailed={designerState.isRefetchError}
      />
    );
  } else if (siteAddressCentre) {
    body = (
      <ConfirmLocationView
        projectId={projectId}
        siteAddressCentre={siteAddressCentre}
        onCancel={onExit}
        isAnalysing={isAnalysing}
        isUnavailable={
          (!explicitAnalysis || hasRequestedAnalysis) &&
          generation?.status === 'READY' &&
          !generation.result?.available
        }
        error={generationError}
        isCreatingManualDesign={createManualDesign.isPending}
        manualDesignError={
          createManualDesign.isError
            ? 'The hand-drawn design could not be created. Please try again.'
            : null
        }
        onDrawManually={(centre) => void onDrawManually(centre)}
        onConfirm={(centre) =>
          requestGeneration({ projectId, location: centre })
        }
      />
    );
  } else {
    body = (
      <div className="flex h-full flex-col items-center justify-center gap-4 p-8">
        <EmptyState text="The project needs a geocoded site address before its solar potential can be checked." />
        <Button size="sm" variant="secondary" onClick={onExit}>
          Back to the project
        </Button>
      </div>
    );
  }

  return (
    <AuthenticatedPageWrapper breadcrumbItems={breadcrumbItems} scrollLock>
      {body}
    </AuthenticatedPageWrapper>
  );
};

const SolarDesignerPage: FC = () => {
  const { projectId } = useParams<{ projectId: string }>();
  const { search } = useLocation();
  const designId = new URLSearchParams(search).get('design') ?? 'default';
  return (
    <SolarDesigner
      key={`${projectId}:${designId}:${new URLSearchParams(search).get('analyse') ?? ''}`}
      projectId={projectId}
    />
  );
};

export default SolarDesignerPage;
